import { Directive, ElementRef, HostBinding, HostListener } from '@angular/core';

// Adds drag-and-drop to an EXISTING `<input type="file">` without touching
// any component's upload logic — wrap the input (and its preview <img>, if
// any) with `<div appFileDropzone>`, and a dropped file is handed to the
// native input via the DataTransfer API, then a real `change` event is
// dispatched on it. Every component's existing `(change)="onFileChange($event)"`
// handler fires exactly as it already does for a manually-picked file — this
// is purely a UX layer on top of 26 different forms' upload fields, not a
// rewrite of any of them.
@Directive({
  selector: '[appFileDropzone]',
})
export class FileDropzoneDirective {
  @HostBinding('class.vw-file-drop') alwaysOn = true;
  @HostBinding('class.is-dragover') isDragover = false;

  constructor(private el: ElementRef<HTMLElement>) {}

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    if (!this.findFileInput()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.isDragover = true;
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragover = false;
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    const input = this.findFileInput();
    if (!input) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.isDragover = false;

    const files = event.dataTransfer && event.dataTransfer.files;
    if (!files || files.length === 0) {
      return;
    }
    const accept = input.getAttribute('accept');
    const file = files[0];
    if (accept && accept.includes('image') && file.type && !file.type.startsWith('image/')) {
      return; // dropped something that isn't an image on an image-only field — silently ignore
    }

    try {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      input.files = dataTransfer.files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } catch {
      // DataTransfer construction can fail on very old browsers — the plain
      // click-to-browse input still works regardless, so this is a silent no-op.
    }
  }

  private findFileInput(): HTMLInputElement | null {
    return this.el.nativeElement.querySelector('input[type="file"]');
  }
}
